import { useState, useEffect } from 'react';
import { getUserLevelFromStorage, hasMinimumLevel, getLevelNumeric, type UserLevel } from '../utils/userLevelUtils';
import { validateAdminStatus, getUserProfile } from '../utils/authUtils';

/**
 * Hook for accessing and monitoring user level state
 * 
 * Returns:
 * - userLevel: Current user's level (LEVEL_0, LEVEL_1, or LEVEL_2)
 * - isAuthenticated: Whether user is logged in
 * - hasMinimumLevel: Function to check if user meets minimum level requirement
 * - levelNumeric: Numeric representation of current level (0, 1, or 2)
 * - refresh: Function to manually refresh user level from storage
 */
export function useUserLevel() {
    // Initialize synchronously from storage to prevent flash of 'LEVEL_0' state on first render
    const [userLevel, setUserLevel] = useState<UserLevel>(() => getUserLevelFromStorage());
    const [isAuthenticated, setIsAuthenticated] = useState(() => localStorage.getItem('isAuthenticated') === 'true');
    const [isLoading, setIsLoading] = useState(false);

    const refresh = async () => {
        // 1. Read from storage again (in case it changed)
        const level = getUserLevelFromStorage();
        const isAuth = localStorage.getItem('isAuthenticated') === 'true';

        // Update state if different (though initial state should catch it)
        if (level !== userLevel) setUserLevel(level);
        if (isAuth !== isAuthenticated) setIsAuthenticated(isAuth);

        // 2. If authenticated, verify/sync with server in background
        if (isAuth) {
            // Use the imported getUserProfile which has request deduplication
            try {
                const response = await getUserProfile();

                if (response.success && response.data) {
                    const serverLevel = response.data.user_level as UserLevel;
                    // Only update if changed prevents unnecessary re-renders
                    if (serverLevel && serverLevel !== level) {
                        // LocalStorage is already updated by getUserProfile
                        setUserLevel(serverLevel);
                    }
                }
            } catch (error) {
                console.error('Failed to sync user level:', error);
            }
        }
    };

    useEffect(() => {
        // Initial load
        refresh();

        // Listen for auth state changes
        const handleAuthChange = () => {
            refresh();
        };

        window.addEventListener('authStateChange', handleAuthChange);

        // Also listen for storage changes
        window.addEventListener('storage', handleAuthChange);

        return () => {
            window.removeEventListener('authStateChange', handleAuthChange);
            window.removeEventListener('storage', handleAuthChange);
        };
    }, []);

    return {
        userLevel,
        isAuthenticated,
        hasMinimumLevel: (minLevel: UserLevel) => hasMinimumLevel(userLevel, minLevel),
        levelNumeric: getLevelNumeric(userLevel),
        refresh
    };
}
